'use client';

import { useClientTranslation } from '@lib/i18n/client';
import { DateTime } from 'luxon';

import {
  ManifestBirthdateField,
  ManifestCheckboxField,
  ManifestInputField,
  ManifestSelectField,
} from '../fields';
import { ManifestSection } from '../ManifestSection';
import { mapManifestOptionsToHtmlOptions } from '../utils';

import type { Manifest, ManifestFormValues } from '../types';
import type { SimpleFormApi } from '@lib/form/helper';
import type { AnyFieldApi } from '@tanstack/react-form';
import type { I18NInterface } from '@tuic/web-compositions/utils/i18n';
import type { FC } from 'react';

export type IdCardSectionProps = {
  form: SimpleFormApi<ManifestFormValues>;
  manifest?: Manifest;
  isDisabled?: boolean;
  entryRequirementsHref?: string;
  /** Gate for the UK-ETA confirmation checkbox. */
  showEtaCheckbox: boolean;
  /** Gate for the USA/ESTA confirmation checkbox. */
  showUsaCheckbox: boolean;

  i18n?: I18NInterface<
    | 'idCardTitle'
    | 'idCardDescription'
    | 'idCardDescriptionMultipleTypes'
    | 'idCardType'
    | 'idCardNationality'
    | 'idCardNumber'
    | 'idCardBirthPlace'
    | 'idCardIssuingPlace'
    | 'idCardBirthDate'
    | 'idCardIssuingDate'
    | 'idCardBirthName'
    | 'idCardValidUntil'
    | 'idCardProfession'
    | 'idCardProfessionNote'
    | 'idCardEntryRequirementsTitle'
    | 'idCardEntryRequirementsConfirmBefore'
    | 'idCardEntryRequirementsConfirmLink'
    | 'idCardEntryRequirementsConfirmAfter'
    | 'idCardVisaConfirm'
    | 'idCardHasETAEligibleHeading'
    | 'idCardHasETAEligibleBefore'
    | 'idCardHasETABoldTerm'
    | 'idCardHasETAEligibleAfter'
    | 'idCardHasETAIneligibleHeading'
    | 'idCardHasETAIneligibleBefore'
    | 'idCardHasETAIneligibleAfter'
    | 'idCardSelectPlaceholder'
  >;
};

export const IdCardSection: FC<IdCardSectionProps> = ({
  form,
  i18n,
  manifest,
  isDisabled,
  entryRequirementsHref,
  showEtaCheckbox,
  showUsaCheckbox,
}) => {
  const { i18n: i18next } = useClientTranslation();
  const locale = i18next.resolvedLanguage;
  const credentialOptions = manifest?.manifestOptions?.credentials;
  const now = DateTime.now().year;

  const hasMultiplePassportTypes =
    (credentialOptions?.passportTypeOptions?.length ?? 0) > 1;

  return (
    <ManifestSection pictogram="blog" headline={i18n?.idCardTitle}>
      <p>
        {hasMultiplePassportTypes
          ? i18n?.idCardDescriptionMultipleTypes
          : i18n?.idCardDescription}
      </p>

      <div className="flex flex-col gap-4 md:flex-row md:gap-8">
        <form.Field name="credentials.passportType">
          {(field: AnyFieldApi) => (
            <ManifestSelectField
              field={field}
              required
              label={i18n?.idCardType}
              placeholder={i18n?.idCardSelectPlaceholder}
              options={mapManifestOptionsToHtmlOptions(
                credentialOptions?.passportTypeOptions,
              )}
              className="w-full"
              disabled={isDisabled}
            />
          )}
        </form.Field>
        <form.Field name="credentials.nationality">
          {(field: AnyFieldApi) => (
            <ManifestSelectField
              field={field}
              required
              label={i18n?.idCardNationality}
              placeholder={i18n?.idCardSelectPlaceholder}
              options={mapManifestOptionsToHtmlOptions(
                credentialOptions?.nationalityOptions,
              )}
              className="w-full"
              disabled={isDisabled}
            />
          )}
        </form.Field>
      </div>
      <div className="flex flex-col gap-4 md:flex-row md:gap-8">
        <form.Field name="credentials.passportNumber">
          {(field: AnyFieldApi) => (
            <ManifestInputField
              field={field}
              required
              label={i18n?.idCardNumber}
              className="w-full"
              disabled={isDisabled}
            />
          )}
        </form.Field>
        <form.Field name="credentials.placeOfBirth">
          {(field: AnyFieldApi) => (
            <ManifestInputField
              field={field}
              required
              label={i18n?.idCardBirthPlace}
              className="w-full"
              disabled={isDisabled}
            />
          )}
        </form.Field>
      </div>
      <div className="flex flex-col gap-4 md:flex-row md:gap-8">
        <form.Field name="credentials.placeOfIssue">
          {(field: AnyFieldApi) => (
            <ManifestInputField
              field={field}
              required
              label={i18n?.idCardIssuingPlace}
              className="w-full"
              disabled={isDisabled}
            />
          )}
        </form.Field>
        <form.Field name="credentials.dateOfBirth">
          {(field: AnyFieldApi) => (
            <ManifestBirthdateField
              field={field}
              required
              label={i18n?.idCardBirthDate}
              locale={locale}
              minYear={now - 100}
              maxYear={now}
              className="w-full"
              disabled={isDisabled}
            />
          )}
        </form.Field>
      </div>
      <div className="flex flex-col gap-4 md:flex-row md:gap-8">
        <form.Field name="credentials.dateOfIssue">
          {(field: AnyFieldApi) => (
            <ManifestBirthdateField
              field={field}
              required
              label={i18n?.idCardIssuingDate}
              locale={locale}
              maxYear={now}
              className="w-full"
              disabled={isDisabled}
            />
          )}
        </form.Field>
        <form.Field name="credentials.birthName">
          {(field: AnyFieldApi) => (
            <ManifestInputField
              field={field}
              label={i18n?.idCardBirthName}
              className="w-full"
              disabled={isDisabled}
            />
          )}
        </form.Field>
      </div>
      <div className="flex flex-col gap-4 md:flex-row md:gap-8">
        <form.Field name="credentials.validUntil">
          {(field: AnyFieldApi) => (
            <ManifestBirthdateField
              field={field}
              required
              label={i18n?.idCardValidUntil}
              locale={locale}
              minYear={now}
              maxYear={now + 20}
              className="w-full"
              disabled={isDisabled}
            />
          )}
        </form.Field>
        <form.Field name="credentials.profession">
          {(field: AnyFieldApi) => (
            <ManifestSelectField
              field={field}
              required
              label={i18n?.idCardProfession}
              note={i18n?.idCardProfessionNote}
              placeholder={i18n?.idCardSelectPlaceholder}
              options={mapManifestOptionsToHtmlOptions(
                credentialOptions?.professionOptions,
              )}
              className="w-full"
              disabled={isDisabled}
            />
          )}
        </form.Field>
      </div>

      <div className="mt-4 space-y-4">
        <h3 className="headline-md font-normal">
          {i18n?.idCardEntryRequirementsTitle}*
        </h3>
        <form.Field name="credentials.entryRequirementsAccepted">
          {(field: AnyFieldApi) => (
            <ManifestCheckboxField
              field={field}
              label={
                <>
                  {i18n?.idCardEntryRequirementsConfirmBefore}
                  {entryRequirementsHref ? (
                    <a
                      href={entryRequirementsHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                    >
                      {i18n?.idCardEntryRequirementsConfirmLink}
                    </a>
                  ) : (
                    i18n?.idCardEntryRequirementsConfirmLink
                  )}
                  {i18n?.idCardEntryRequirementsConfirmAfter}
                </>
              }
              disabled={isDisabled}
            />
          )}
        </form.Field>
        {showUsaCheckbox && (
          <form.Field name="credentials.hasVisa">
            {(field: AnyFieldApi) => (
              <ManifestCheckboxField
                field={field}
                label={i18n?.idCardVisaConfirm}
                disabled={isDisabled}
              />
            )}
          </form.Field>
        )}
        {showEtaCheckbox && (
          <form.Field name="credentials.hasETA">
            {(field: AnyFieldApi) => (
              <ManifestCheckboxField
                field={field}
                label={
                  <>
                    <u>{i18n?.idCardHasETAEligibleHeading}</u>{' '}
                    {i18n?.idCardHasETAEligibleBefore}{' '}
                    <b>{i18n?.idCardHasETABoldTerm}</b>{' '}
                    {i18n?.idCardHasETAEligibleAfter}
                    <br />
                    <br />
                    <u>{i18n?.idCardHasETAIneligibleHeading}</u>{' '}
                    {i18n?.idCardHasETAIneligibleBefore}{' '}
                    <b>{i18n?.idCardHasETABoldTerm}</b>{' '}
                    {i18n?.idCardHasETAIneligibleAfter}
                  </>
                }
                disabled={isDisabled}
              />
            )}
          </form.Field>
        )}
      </div>
    </ManifestSection>
  );
};
